
import { FSharpRef, Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { array_type, list_type, record_type, string_type, int32_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { PortType, isGeneratedSheetName, WaveIndexT, PortType_$reflection } from "../../Common/CommonTypes.js";
import { FastSimulation__get_SimulatedTopSheet, SimulatedDesign__LabelsOfInstance_Z47F38896, SheetPort, SimulatedDesign__ComponentOfInstance_6546966D, FastSimulation__ComponentOf_6546966D, SimulatedDesign__SheetOfInstance_1A26531 } from "../SimTypes.js";
import { map } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { map as map_2, defaultArg } from "../../fable_modules/fable-library-js.5.13.0/Option.js";
import { length, map as map_1, append, ofArray, mapIndexed, tryItem, isEmpty, collect, empty, choose, tryFind } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { memoizeByIdentity } from "../../Common/Helpers.js";
import { containsKey, empty as empty_1, tryFind as tryFind_1, ofList } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { List_countBy } from "../../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { defaultOf, safeHash, equals, curry2, createAtom, compare, comparePrimitives, stringHash } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { Constants_copiesShownFlattened } from "../ArrayElaborate.js";
import { caseCompAndPortName, getInputNameW, getOutputNameW } from "./WaveNames.js";
import { toList } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../../fable_modules/fable-library-js.5.13.0/Range.js";
import { Dictionary } from "../../fable_modules/fable-library-js.5.13.0/MutableMap.js";
import { tryGetValue } from "../../fable_modules/fable-library-js.5.13.0/MapUtil.js";

/**
 * One port of one instance, with everything a waveform needs that the design cannot supply.
 * 
 * The field names are prefixed because a record field resolves to the last type declaring one,
 * and the wave simulator is full of ports, widths and names.
 */
export class InstancePort extends Record {
    constructor(PortComp, PortIs, PortNum, PortArrayIndex, PortDriver, PortWidth, PortDisplayName, PortLabel, PortCompLabel) {
        super();
        this.PortComp = PortComp;
        this.PortIs = PortIs;
        this.PortNum = (PortNum | 0);
        this.PortArrayIndex = PortArrayIndex;
        this.PortDriver = PortDriver;
        this.PortWidth = (PortWidth | 0);
        this.PortDisplayName = PortDisplayName;
        this.PortLabel = PortLabel;
        this.PortCompLabel = PortCompLabel;
    }
}

export function InstancePort_$reflection() {
    return record_type("PortView.InstancePort", [], InstancePort, () => [["PortComp", int32_type], ["PortIs", PortType_$reflection()], ["PortNum", int32_type], ["PortArrayIndex", int32_type], ["PortDriver", int32_type], ["PortWidth", int32_type], ["PortDisplayName", string_type], ["PortLabel", string_type], ["PortCompLabel", string_type]]);
}

/**
 * What one instance offers, with the facts that are about the INSTANCE rather than about any one
 * of its ports worked out once. Both were being recomputed per wave, and each walks the instance
 * path - which made reconciling a hundred waves quadratic in the depth of the design for no
 * reason at all.
 */
export class InstanceView extends Record {
    constructor(ViewSheet, ViewSubSheet, ViewPorts) {
        super();
        this.ViewSheet = ViewSheet;
        this.ViewSubSheet = ViewSubSheet;
        this.ViewPorts = ViewPorts;
    }
}

export function InstanceView_$reflection() {
    return record_type("PortView.InstanceView", [], InstanceView, () => [["ViewSheet", string_type], ["ViewSubSheet", list_type(string_type)], ["ViewPorts", list_type(InstancePort_$reflection())]]);
}

/**
 * The width and driver index of one port: what a wave needs from the BUILD, and nothing else.
 * 
 * Two ints, because this is what travels when the simulator is in another process. Everything
 * else a selector shows - names, labels, which ports carry a wave at all - is a fact about the
 * design, which the renderer holds; only elaboration facts need the simulation, and these are
 * they. The driver index doubles as the build's read handle: `SimRead` accepts it for as long as
 * this build lives, and the next build invalidates it wholesale.
 */
export class PortSlot extends Record {
    constructor(SlotWidth, SlotDriver) {
        super();
        this.SlotWidth = (SlotWidth | 0);
        this.SlotDriver = SlotDriver;
    }
}

export function PortSlot_$reflection() {
    return record_type("PortView.PortSlot", [], PortSlot, () => [["SlotWidth", int32_type], ["SlotDriver", int32_type]]);
}

/**
 * Every port of one component of one instance, positionally: array index = port number, which
 * is the design's own numbering (a Component's port lists are position-numbered).
 */
export class ComponentSlots extends Record {
    constructor(SlotsComp, SlotsIns, SlotsOuts) {
        super();
        this.SlotsComp = SlotsComp;
        this.SlotsIns = SlotsIns;
        this.SlotsOuts = SlotsOuts;
    }
}

export function ComponentSlots_$reflection() {
    return record_type("PortView.ComponentSlots", [], ComponentSlots, () => [["SlotsComp", int32_type], ["SlotsIns", array_type(PortSlot_$reflection())], ["SlotsOuts", array_type(PortSlot_$reflection())]]);
}

/**
 * Width and driver of every port of every component on one instance's sheet.
 * 
 * ALL ports, positionally - not the wave-carrying subset. Filtering would mean naming what was
 * kept, which costs more than the entries it saves, and the rule for what carries a wave is a
 * design fact the caller already has - the IOLabel election included, which is read off the
 * design's connections (see `ofSlice`). A group's members share their arrays here, so the DATA
 * is the same whichever member is asked about.
 * 
 * A width of 0 is a port with no signal: an unconnected input, still holding the dummy array it
 * was created with.
 * 
 * This is the sidecar's answer to "what does this instance offer" and the renderer's local
 * answer to the same question, from one body of code - which is the point.
 */
export function sheetSliceOf(fs, instance) {
    let option_3;
    const instance_1 = instance;
    const sheet = SimulatedDesign__SheetOfInstance_1A26531(fs.Design, instance_1);
    const slotsOf = (arrays) => map((io) => (new PortSlot(io.Width, io.Index)), arrays);
    return defaultArg((option_3 = tryFind((ldc) => (ldc.Name === sheet), fs.Design.DesignSheets), (option_3 != null) ? choose((comp) => {
        let fc;
        const option_1 = FastSimulation__ComponentOf_6546966D(fs, [comp.Id, instance_1]);
        if (option_1 != null) {
            return (fc = option_1, new ComponentSlots(comp.Id, slotsOf(fc.InputLinks), slotsOf(fc.Outputs)));
        }
        else {
            return undefined;
        }
    }, option_3.CanvasState[0]) : undefined), empty());
}

/**
 * The wave index of a port: how the rest of Issie names one, and what a selection is made of.
 */
export function waveIndexOf(instance, port) {
    return new WaveIndexT(port.PortArrayIndex, [port.PortComp, instance], port.PortIs, port.PortNum);
}

function carriesWaveOfSlice(compType, inSubSheet, elected, pType) {
    let matchResult;
    switch (compType.tag) {
        case 3: {
            if (pType.tag === 0) {
                matchResult = 0;
            }
            else if (!elected) {
                matchResult = 2;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 0: {
            if (pType.tag === 0) {
                matchResult = 0;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 2: {
            if (pType.tag === 0) {
                matchResult = 0;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 4: {
            if (pType.tag === 0) {
                matchResult = 0;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 1: {
            if (pType.tag === 0) {
                matchResult = 0;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 7: {
            matchResult = 1;
            break;
        }
        default:
            matchResult = 3;
    }
    switch (matchResult) {
        case 0:
            return false;
        case 1:
            return false;
        case 2:
            return false;
        default: {
            let matchResult_1;
            switch (compType.tag) {
                case 28:
                case 6:
                case 27:
                case 29:
                case 30:
                case 54:
                case 7: {
                    matchResult_1 = 0;
                    break;
                }
                case 1: {
                    if (inSubSheet) {
                        matchResult_1 = 1;
                    }
                    else {
                        matchResult_1 = 3;
                    }
                    break;
                }
                case 0: {
                    if (inSubSheet) {
                        matchResult_1 = 2;
                    }
                    else {
                        matchResult_1 = 3;
                    }
                    break;
                }
                default:
                    matchResult_1 = 3;
            }
            switch (matchResult_1) {
                case 0:
                    return false;
                case 1:
                    return false;
                case 2:
                    return false;
                default:
                    return true;
            }
        }
    }
}

const copiesOfBodySheet = memoizeByIdentity((design) => ofList(List_countBy((x) => x, collect((ldc) => choose((comp) => {
    const matchValue = comp.Type;
    let matchResult, ct_1;
    if (matchValue.tag === 26) {
        if (isGeneratedSheetName(matchValue.fields[0].Name)) {
            matchResult = 0;
            ct_1 = matchValue.fields[0];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return ct_1.Name;
        default:
            return undefined;
    }
}, ldc.CanvasState[0]), design.DesignSheets), {
    Equals: (x_1, y) => (x_1 === y),
    GetHashCode: (x_1) => (stringHash(x_1) | 0),
}), {
    Compare: (x_2, y_1) => (comparePrimitives(x_2, y_1) | 0),
}));

function tooManyToList(design, bodySheet) {
    const matchValue = tryFind_1(bodySheet, copiesOfBodySheet(design));
    if (matchValue == null) {
        return false;
    }
    else {
        return matchValue > Constants_copiesShownFlattened;
    }
}

function ioOfferedOnParent(design, sheet, ap) {
    if (!isEmpty(ap)) {
        return !tooManyToList(design, sheet);
    }
    else {
        return false;
    }
}

function offersItsPorts(design, comp) {
    const matchValue = comp.Type;
    if (matchValue.tag === 26) {
        return !tooManyToList(design, matchValue.fields[0].Name);
    }
    else {
        return true;
    }
}

/**
 * The component a wave should take the user TO, which is not always the one it comes from.
 * 
 * A wave on one of an array component's copies comes from a component the EXPANSION made - the
 * copy, which is a custom component on a sheet nobody drew - so there is no symbol anywhere to go
 * to, and the waveform viewer's "show me this" button did nothing at all. What the user means by
 * it is the join, or the input or output, that this port IS: that is drawn on the array
 * component's own sheet, and the body sheet keeps the ids of what is drawn there, so the body's
 * IO component for this port is the drawn one.
 * 
 * Found by port NUMBER and not by label. A copy's port labels are the channels that copy is
 * joined by, which is the whole point of them, and those are not the body's labels - see
 * ArrayElaborate's copyPortNames.
 */
export function drawnComponentOf(design, waveId) {
    let option_5, body, patternInput, option_3, tupledArg, option_1;
    const compId = waveId.Id[0];
    const matchValue = SimulatedDesign__ComponentOfInstance_6546966D(design, waveId.Id);
    let matchResult, ct_1;
    if (matchValue != null) {
        if (matchValue.Type.tag === 26) {
            if (isGeneratedSheetName(matchValue.Type.fields[0].Name)) {
                matchResult = 0;
                ct_1 = matchValue.Type.fields[0];
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return defaultArg((option_5 = tryFind((ldc) => (ldc.Name === ct_1.Name), design.DesignSheets), (option_5 != null) ? ((body = option_5, (patternInput = ((waveId.PortType.tag === 1) ? [body.OutputLabels, (t_1) => (t_1.tag === 1)] : [body.InputLabels, (t) => (t.tag === 0)]), (option_3 = tryItem(waveId.PortNumber, patternInput[0]), (option_3 != null) ? ((tupledArg = option_3, (option_1 = tryFind((comp) => {
                if (comp.Label === tupledArg[0]) {
                    return patternInput[1](comp.Type);
                }
                else {
                    return false;
                }
            }, body.CanvasState[0]), (option_1 != null) ? option_1.Id : undefined))) : undefined)))) : undefined), compId);
        default:
            return compId;
    }
}

/**
 * two ints a port, and it merely happens that a .NET simulator computed the ints.
 */
export function ofSlice(design, instance, slice) {
    const instance_1 = instance;
    const sheet = SimulatedDesign__SheetOfInstance_1A26531(design, instance_1);
    const compsById = defaultArg(tryFind_1(sheet, design.DesignComponentsById), empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }));
    const inSubSheet = ioOfferedOnParent(design, sheet, instance_1);
    const ports = collect((slots) => {
        const matchValue_1 = tryFind_1(slots.SlotsComp, compsById);
        if (matchValue_1 != null) {
            const comp_1 = matchValue_1;
            const insWidths = map((s) => (s.SlotWidth | 0), slots.SlotsIns, Int32Array);
            const outsWidths = map((s_1) => (s_1.SlotWidth | 0), slots.SlotsOuts, Int32Array);
            const portsOf = (portType, portSlots) => {
                let matchValue;
                if (!offersItsPorts(design, comp_1) ? true : !carriesWaveOfSlice(comp_1.Type, inSubSheet, (matchValue = tryItem(0, comp_1.InputPorts), (matchValue != null) && containsKey(new SheetPort(sheet, matchValue), design.DesignConnectionsByPort)), portType)) {
                    return empty();
                }
                else {
                    return mapIndexed((pn, slot) => {
                        const patternInput = (portType.tag === 1) ? [getOutputNameW(true, comp_1.Type, comp_1.Label, insWidths, outsWidths, pn), getOutputNameW(false, comp_1.Type, comp_1.Label, insWidths, outsWidths, pn)] : [getInputNameW(true, comp_1.Type, comp_1.Label, insWidths, outsWidths, pn), getInputNameW(false, comp_1.Type, comp_1.Label, insWidths, outsWidths, pn)];
                        return new InstancePort(slots.SlotsComp, portType, pn, slot.SlotDriver, slot.SlotDriver, slot.SlotWidth, caseCompAndPortName(patternInput[0]), patternInput[1], comp_1.Label);
                    }, ofArray(portSlots));
                }
            };
            return append(portsOf(PortType.Output, slots.SlotsOuts), portsOf(PortType.Input, slots.SlotsIns));
        }
        else {
            return empty();
        }
    }, slice);
    return new InstanceView(sheet, map_1((label) => label.toLocaleUpperCase(), SimulatedDesign__LabelsOfInstance_Z47F38896(design, instance_1, "?")), ports);
}

export let sliceSource = createAtom(undefined);

function trySliceOf(fs, instance) {
    const isCarrier = (fs.NumStepArrays === 0) && (FastSimulation__get_SimulatedTopSheet(fs) !== "");
    let matchResult, fetched_1;
    if (sliceSource() == null) {
        if (isCarrier) {
            matchResult = 1;
        }
        else {
            matchResult = 2;
        }
    }
    else if ((map_2(curry2, sliceSource()), isCarrier)) {
        matchResult = 0;
        fetched_1 = map_2(curry2, sliceSource());
    }
    else {
        matchResult = 2;
    }
    switch (matchResult) {
        case 0:
            return fetched_1(fs)(instance);
        case 1:
            return undefined;
        default:
            return sheetSliceOf(fs, instance);
    }
}

/**
 * The wave-carrying ports of one instance, from the DESIGN alone - no simulation, no slices.
 * 
 * Every wave comes back UNRESOLVED (SimArrayIndex = DriverIndex -1): where its data lies is a build fact
 * this deliberately does not know. Reconciliation resolves them against whichever simulator
 * runs - which is what makes this enumeration mode-agnostic, and what the dev harness selects
 * with. Port counts and numbering are the design's own; the carries-a-wave rule is the same one
 * ofSlice applies, election by connection included. The one thing a slice adds - dropping an
 * unconnected input's width-0 port - is left to resolution, where the fetch already records
 * such a wave as having no driver.
 */
export function waveIndicesOfDesign(design, instance) {
    let option_1;
    const instance_1 = instance;
    const ap = instance_1;
    const sheet = SimulatedDesign__SheetOfInstance_1A26531(design, instance_1);
    const inSubSheet = ioOfferedOnParent(design, sheet, ap);
    return defaultArg((option_1 = tryFind((ldc) => (ldc.Name === sheet), design.DesignSheets), (option_1 != null) ? collect((comp_1) => {
        const portsOf = (portType, count) => {
            let matchValue;
            if (!offersItsPorts(design, comp_1) ? true : !carriesWaveOfSlice(comp_1.Type, inSubSheet, (matchValue = tryItem(0, comp_1.InputPorts), (matchValue != null) && containsKey(new SheetPort(sheet, matchValue), design.DesignConnectionsByPort)), portType)) {
                return empty();
            }
            else {
                return map_1((pn) => (new WaveIndexT(-1, [comp_1.Id, ap], portType, pn)), toList(rangeDouble(0, 1, count - 1)));
            }
        };
        return append(portsOf(PortType.Output, length(comp_1.OutputPorts)), portsOf(PortType.Input, length(comp_1.InputPorts)));
    }, option_1.CanvasState[0]) : undefined), empty());
}

/**
 * The ports of one instance that carry a waveform, from the simulation in this process.
 * 
 * The DERIVATION is `ofSlice`'s - the design plus two ints a port - and this is only the local
 * composition of it with the local slice. A renderer whose simulator is in another process runs
 * the same `ofSlice` over a slice that arrived on the wire, and the two must be the same
 * function or the two modes drift; that is why there is no second derivation here.
 */
export function ofInstance(fs, instance) {
    return ofSlice(fs.Design, instance, sheetSliceOf(fs, instance));
}

/**
 * The raw name of one port of one instance - component label, port name, bit limits, uncased -
 * which is what the probe label prints beside a value. The selector's rows use the cased
 * `PortDisplayName` instead.
 */
export function nameOfPort(fs, wi) {
    let option_2, slots_1, insWidths, outsWidths;
    const patternInput = wi.Id;
    const matchValue = SimulatedDesign__ComponentOfInstance_6546966D(fs.Design, wi.Id);
    if (matchValue != null) {
        const comp = matchValue;
        return defaultArg((option_2 = tryFind((slots) => equals(slots.SlotsComp, patternInput[0]), defaultArg(trySliceOf(fs, patternInput[1]), empty())), (option_2 != null) ? ((slots_1 = option_2, (insWidths = map((s) => (s.SlotWidth | 0), slots_1.SlotsIns, Int32Array), (outsWidths = map((s_1) => (s_1.SlotWidth | 0), slots_1.SlotsOuts, Int32Array), (wi.PortType.tag === 1) ? getOutputNameW(true, comp.Type, comp.Label, insWidths, outsWidths, wi.PortNumber) : getInputNameW(true, comp.Type, comp.Label, insWidths, outsWidths, wi.PortNumber))))) : undefined), "?");
    }
    else {
        return "?";
    }
}

export const tryOfInstanceCached = memoizeByIdentity((fs) => {
    const held = new Dictionary([], {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    });
    return (instance) => {
        let ports_1;
        let matchValue;
        let outArg = defaultOf();
        matchValue = [tryGetValue(held, instance, new FSharpRef(() => outArg, (v) => {
            outArg = v;
        })), outArg];
        if (matchValue[0]) {
            return matchValue[1];
        }
        else {
            const option_1 = trySliceOf(fs, instance);
            return (option_1 != null) ? ((ports_1 = ofSlice(fs.Design, instance, option_1), (held.set(instance, ports_1), ports_1))) : undefined;
        }
    };
});

function emptyView(fs, instance) {
    return new InstanceView(SimulatedDesign__SheetOfInstance_1A26531(fs.Design, instance), empty(), empty());
}

export function ofInstanceCached(fs, instance) {
    return defaultArg(tryOfInstanceCached(fs)(instance), emptyView(fs, instance));
}

